import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  Suspense,
} from "react";
import { AiOutlineQrcode } from "react-icons/ai"; // ⭐ NEW QR Scanner Icon

// Lazy-load scanner to keep bundle small
const TicketScanner = React.lazy(() => import("./TicketScanner"));

function safeHex(h) {
  if (!h) return null;
  const s = String(h).trim();
  return s.startsWith("#") ? s : `#${s}`;
}

function hexToRgb(hex) {
  const h = safeHex(hex);
  if (!h) return null;
  const cleaned = h.replace("#", "");
  const normalized =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  const bigint = parseInt(normalized, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

function darkenHex(hex, amount = 0.12) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/* Convert server-relative path to absolute browser URL.
   If the value already starts with http(s) or data:, leave as-is.
*/
function toAbsolute(url) {
  if (!url) return url;
  const s = String(url).trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("data:")) return s;
  // Ensure leading slash
  const path = s.startsWith("/") ? s : `/${s}`;
  try {
    return `${window.location.origin}${path}`;
  } catch {
    return path;
  }
}

export default function Topbar({
  onToggleSidebar = () => {},
  showHamburger = false,
}) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [logo, setLogo] = useState("/images/logo.png");
  const [primaryColor, setPrimaryColor] = useState("#196e87");

  const openScanner = useCallback(() => setScannerOpen(true), []);
  const closeScanner = useCallback(() => setScannerOpen(false), []);

  // close on ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") closeScanner();
    }
    if (scannerOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scannerOpen, closeScanner]);

  // load admin topbar config
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    async function loadConfig() {
      try {
        const res = await fetch("/api/admin-config", {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const json = await res.json().catch(() => null);
          if (mounted && json && typeof json === "object") {
            if (json.logoUrl) setLogo(toAbsolute(json.logoUrl));
            if (json.primaryColor)
              setPrimaryColor(safeHex(json.primaryColor) || "#196e87");
            try {
              window.localStorage.setItem(
                "admin:topbar",
                JSON.stringify({
                  logoUrl: json.logoUrl || "",
                  primaryColor: json.primaryColor || "",
                }),
              );
            } catch {}
            return;
          }
        }
      } catch (e) {
        // failed to fetch, fallback to local storage
      }

      try {
        const raw = window.localStorage.getItem("admin:topbar");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.logoUrl) setLogo(toAbsolute(parsed.logoUrl));
          if (parsed?.primaryColor)
            setPrimaryColor(safeHex(parsed.primaryColor) || "#196e87");
        }
      } catch {}
    }

    loadConfig();
    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  // Listen for live updates made by AdminTopbarSettings (or other pages)
  useEffect(() => {
    function onTopbarUpdate(e) {
      try {
        const d = e && e.detail ? e.detail : null;
        if (!d) return;
        if (d.logoUrl) setLogo(toAbsolute(d.logoUrl));
        if (d.primaryColor)
          setPrimaryColor(safeHex(d.primaryColor) || "#196e87");
      } catch {}
    }
    window.addEventListener("admin:topbar-updated", onTopbarUpdate);
    // some code used custom event name admin:topbar-updated, keep compatibility with older event names if used
    window.addEventListener("admin:topbar-update", onTopbarUpdate); // legacy
    return () => {
      window.removeEventListener("admin:topbar-updated", onTopbarUpdate);
      window.removeEventListener("admin:topbar-update", onTopbarUpdate);
    };
  }, []);

  const buttonBg = darkenHex(primaryColor, 0.14);

  return (
    <>
      <header
        className="h-16 w-full flex items-center px-4 md:px-6 shadow"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="flex items-center w-full">
          {/* Mobile hamburger - only when menus exist (admin pages) */}
          {showHamburger && (
            <button
              onClick={onToggleSidebar}
              className="md:hidden mr-3 p-2 rounded bg-black/10 text-white hover:bg-black/20"
              aria-label="Toggle sidebar"
              title="Open menu"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Logo */}
          <img
            src={logo}
            alt="RailTrans Expo"
            className="h-10 w-auto mr-4"
            style={{ objectFit: "contain" }}
            onError={(e) => {
              try {
                e.currentTarget.src = "/images/logo.png";
              } catch {}
            }}
          />

          <div className="flex-1" />

          {/* Right controls */}
          <div className="flex items-center gap-3">
            {/* ⭐ UPDATED SCANNER BUTTON WITH REACT ICON */}
            <button
              onClick={openScanner}
              className="ml-2 px-3 py-2 rounded text-white font-semibold flex items-center gap-2"
              style={{ backgroundColor: buttonBg }}
              title="Open Gate Scanner"
              aria-label="Open Ticket Scanner"
            >
              <AiOutlineQrcode size={20} /> {/* 👈 NEW ICON */}
              <span className="hidden sm:inline">Scanner</span>
            </button>
          </div>
        </div>
      </header>

      {scannerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeScanner}
            aria-hidden="true"
          />

          <div className="relative w-[96%] sm:w-[80%] md:w-[720px] max-w-full bg-white rounded-lg shadow-2xl overflow-hidden z-10">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div
                className="text-lg font-semibold"
                style={{ color: primaryColor }}
              >
                Ticket Scanner
              </div>

              <button
                className="px-3 py-1 rounded bg-gray-100 text-gray-800"
                onClick={closeScanner}
              >
                Close
              </button>
            </div>

            <div style={{ minHeight: 360 }} className="p-4">
              <Suspense
                fallback={
                  <div
                    className="text-center py-20"
                    style={{ color: primaryColor }}
                  >
                    Loading scanner…
                  </div>
                }
              >
                <TicketScanner
                  apiPath="/api/tickets/scan"
                  onError={(err) => {
                    console.error("Scanner error:", err);
                    // user-friendly message
                    alert(err?.message || "Scanner error");
                  }}
                  onSuccess={(result) => {
                    console.log("Scan success:", result);
                    // small delay so user sees success then close
                    setTimeout(() => closeScanner(), 800);
                  }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
